import qs from "query-string";
import dayjs from "dayjs";

import { ATTRIBUTION_PARAMS } from "../../constants/site";
import { types as appTypes } from "../app/App.redux";
import { breadcrumbsInit } from "../search/helpers/get-breadcrumbs";

/**
 * Action types
 * @property {string} ATTRIBUTION_UPDATED
 */
export const types = {
  ATTRIBUTION_UPDATED: "ATTRIBUTION_UPDATED",
  DATA_LAYER_LISTING_PARAM_CLEAR: "DATA_LAYER_LISTING_PARAM_CLEAR",
  DATA_LAYER_LISTING_PARAM_UPDATE: "DATA_LAYER_LISTING_PARAM_UPDATE",
  DATA_LAYER_SEARCH_PARAM_UPDATE: "DATA_LAYER_SEARCH_PARAM_UPDATE",
};

/**
 * @namespace Reducers
 */
export const reducers = {
  /**
   * Updates attribution state
   * @param {object} state
   * @param {object} action
   * @param {string} action.type
   * @param {array} action.attributionParams
   * @return {state}
   * @memberof Reducers
   */
  attribution: (state = [], action) => {
    switch (action.type) {
      case types.ATTRIBUTION_UPDATED:
        return [...action.attributionParams];
      default:
        return state;
    }
  },

  /**
   * Updates data layer listing parameters state
   * @param {object} state
   * @param {object} action
   * @param {string} action.type
   * @param {object} action.params
   * @return {object} state
   * @memberof Reducers
   */
  dataLayerListingParameters: (
    state = { ...defaultDataLayerListingParameters },
    action
  ) => {
    switch (action.type) {
      case types.DATA_LAYER_LISTING_PARAM_CLEAR:
      case appTypes.ROUTE_CHANGE:
        return { ...defaultDataLayerListingParameters };
      case types.DATA_LAYER_LISTING_PARAM_UPDATE:
        return action.params;
      default:
        return state;
    }
  },

  /**
   * Updates data layer search parameters state
   * @param {object} state
   * @param {object} action
   * @param {string} action.type
   * @param {object} action.params
   * @return {object} state
   * @memberof Reducers
   */
  dataLayerSearchParameters: (
    state = { ...defaultDataLayerSearchParameters },
    action
  ) => {
    switch (action.type) {
      case appTypes.ROUTE_CHANGE:
        return { ...defaultDataLayerSearchParameters };
      case types.DATA_LAYER_SEARCH_PARAM_UPDATE:
        return action.params;
      default:
        return state;
    }
  },
};

/**
 * @namespace Actions
 */
export const actions = {
  /**
   * Add data layer listing parameters
   * @param {array} listings
   * @memberof Actions
   */
  addDataLayerListingParameters: listings => (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      const { analytics } = getState();
      const { dataLayerListingParameters } = analytics;
      const keyId = dataLayerListingPrefix + dataLayerListingFields.equipmentId;
      const params = getDataLayerListingParameters(listings);

      // End early if parameters were already added for this listing
      if (
        !!keyId &&
        !!dataLayerListingParameters?.[keyId] &&
        !!params?.[keyId] &&
        dataLayerListingParameters[keyId] === params[keyId]
      ) {
        resolve();
        return;
      }

      dispatch({
        type: types.DATA_LAYER_LISTING_PARAM_UPDATE,
        params,
      });

      resolve();
    });
  },

  /**
   * Add data layer search parameters
   * @param {array} listings
   * @memberof Actions
   */
  addDataLayerSearchParameters: searchParams => dispatch => {
    const params = getDataLayerSearchParameters(searchParams);

    dispatch({
      type: types.DATA_LAYER_SEARCH_PARAM_UPDATE,
      params,
    });
  },

  /**
   * Manually clear data layer listing parameters
   * @memberof Actions
   */
  clearDataLayerListingParameters: attributionParams => ({
    type: types.DATA_LAYER_LISTING_PARAM_CLEAR,
  }),

  /**
   * Update attribution state if URL contains attribution params
   * @param {string} href
   * @param {string} locationSearch
   * @memberof Actions
   */
  update: (href, locationSearch) => dispatch => {
    if (!!locationSearch) {
      let attributionParams = getAttributionParams(href, locationSearch);

      if (!!attributionParams.length) {
        dispatch(actions.handleUpdate(attributionParams));
      }
    }
  },

  /**
   * Handle attribution updated event
   * @param {array} attributionParams
   * @memberof Actions
   */
  handleUpdate: attributionParams => ({
    type: types.ATTRIBUTION_UPDATED,
    attributionParams,
  }),
};

function getAttributionParams(href, locationSearch) {
  let attributionParams = [];

  try {
    const params = qs.parse(locationSearch);

    if (!!params) {
      attributionParams = Object.keys(params).reduce((ap, key) => {
        if (ATTRIBUTION_PARAMS.includes(key)) {
          if (!ap.length) {
            ap.push({ param: "first_page", value: href });
          }

          ap.push({ param: key, value: params[key] });
        }

        return ap;
      }, []);
    }

    if (!!attributionParams.length) {
      attributionParams.push({
        param: "timestamp",
        value: dayjs().toISOString(),
      });
    }
  } catch (err) {}

  return attributionParams;
}

/**
 * Get data layer listing parameters
 *
 * @param {array} listings
 * @returns {object}
 */
function getDataLayerListingParameters(listings) {
  const fields = dataLayerListingFields;

  try {
    if (!listings || !listings.length) {
      throw new Error("No listings");
    }

    const payload = listings.reduce((params, listing) => {
      const summary = {};

      Object.keys(fields).forEach(key => {
        const dataLayerKey = dataLayerListingPrefix + fields[key];
        const value =
          key === "price"
            ? listing?.salePrice || listing?.advertisedPrice
            : listing[key];

        if (!!value) {
          params[dataLayerKey] = !!params[dataLayerKey]
            ? params[dataLayerKey] + "," + value
            : value;
          summary[fields[key]] = value;
        } else if (!params[dataLayerKey]) {
          params[dataLayerKey] = undefined;
        }
      });

      return params;
    }, {});

    return payload;
  } catch (err) {
    return { ...defaultDataLayerListingParameters };
  }
}

const dataLayerListingPrefix = "listing";

const dataLayerListingFields = {
  year: "Year",
  make: "Make",
  model: "Model",
  locationCity: "City",
  locationState: "State",
  locationZip: "ZipCode",
  serial: "SerialNumber",
  equipmentId: "EquipmentId",
  price: "Price",
  meterValue: "Meter",
  meterType: "MeterType",
  sourceDivision: "SourceDivision",
};

const defaultDataLayerListingParameters = Object.keys(
  dataLayerListingFields
).reduce((params, key) => {
  const dataLayerKey = dataLayerListingPrefix + dataLayerListingFields[key];
  params[dataLayerKey] = undefined;
  return params;
}, {});

/**
 * Get data layer search parameters
 *
 * @param {array} searchParams
 * @returns {object}
 */
function getDataLayerSearchParameters(searchParams = []) {
  try {
    const payload = searchParams.reduce((params, searchParam) => {
      const key = searchParam.key.replace(/ /g, "");
      const dataLayerKey = dataLayerSearchParametersPrefix + key;

      if (searchParam.value !== searchParam.fallbackValue) {
        params[dataLayerKey] = searchParam.value;
      } else {
        params[dataLayerKey] = undefined;
      }

      return params;
    }, {});

    breadcrumbsInit.forEach(bc => {
      const key = bc.key.replace(/ /g, "");
      const dataLayerKey = dataLayerSearchParametersPrefix + key;

      if (!payload[dataLayerKey]) {
        payload[dataLayerKey] = undefined;
      }
    });

    return payload;
  } catch (err) {
    return { ...defaultDataLayerSearchParameters };
  }
}

const dataLayerSearchParametersPrefix = "search";

const defaultDataLayerSearchParameters = breadcrumbsInit.reduce(
  (params, bc) => {
    const key = bc.key.replace(/ /g, "");
    const dataLayerKey = dataLayerSearchParametersPrefix + key;
    params[dataLayerKey] = undefined;
    return params;
  },
  {}
);
